import * as React from 'react'
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router'
import * as Sentry from '@sentry/browser'
import { Button, Callout, Divider, Drawer, DrawerSize, Intent } from '@blueprintjs/core'
import { AxiosPromise } from 'axios'
import { IconNames } from '@blueprintjs/icons'
import Table from './Table'
import { AppState } from '../../../store'
import Widget from '../../../components/Widget'
import { cleanParams, pushUrl, readQueryParams, replaceQueryParams } from '../../../utils/pagination'
import * as Meta from './Meta'
import Poller from './Poller'
import { PaginableMeta } from '../../../types/api/paginable'
import { ListingManagerListingTypes } from '../../../types/api/listings'
import { ColumnParams } from './types'
import StateSwitcher from './StateSwitcher'
import TabStates from '../shopify/constants/TabStates'
import { applyEnabledColumns, DEFAULT_PER_PAGE, visibleCustomizableColumnCount } from './Constants'
import ColumnDialog from './ColumnDialog'
import { getFromStorage } from '../../../storage'
import Client from '../../../client'
import { newConnection, updateFromProduct } from './StatusUpdate'
import PopIn from '../../../components/PopIn'
import CHANNELS from '../../../constants/Channels'
import { BulkActionBar } from './BulkActionBar'
import { trackBulkReviewClickEvent } from './utils'
import config from '../../../config'
import { S } from '../../../utils'
import SelectionManager from './SelectionManager'
import { ConnectedMarketAccount } from '../../../current/types'

export interface DialogState {
  listDialogOpen: boolean
  delistDialogOpen: boolean
  relistDialogOpen: boolean
  purgeDialogOpen: boolean
}

export interface BulkListingDialogsProps {
  selectionManager: SelectionManager
  dialogState: DialogState
  onClose: (stateKey: keyof DialogState) => void
  marketAccountId: number
  onSubmitted: () => void
  channelId?: number
}

export interface ShowListingProps {
  listingId: number | string
  onChange: any
  refreshData: any
  onClose: () => void
  marketAccount: ConnectedMarketAccount
}

interface Props {
  marketAccountId: number
  listingIndexFetchMethod: (
    marketAccountId: number,
    meta: Partial<PaginableMeta>
  ) => AxiosPromise<{
    listings: ListingManagerListingTypes[]
    meta: PaginableMeta
  }>
  tableColumns: (
    meta: PaginableMeta,
    onMetaChange: Function,
    isActive: boolean,
    openDrawer: Function,
    marketAccountId: number
  ) => ColumnParams[]
  ShowListingComponent: FC<ShowListingProps>
  BulkListingDialogs?: FC<BulkListingDialogsProps>
  channelId?: number
  columnStorageKey?: string
}

type SelectionState = {
  selection: Set<any>
  selectAll: boolean
}

export const ListingManager: FC<Props> = ({
  marketAccountId,
  listingIndexFetchMethod,
  tableColumns,
  ShowListingComponent,
  BulkListingDialogs,
  channelId,
  columnStorageKey
}) => {
  const connectedMarketAccounts = useSelector((state: AppState) => state.current.organization.connectedMarketAccounts)
  const currentUser = useSelector((state: AppState) => state.current.user)
  const marketAccount = useMemo(
    () => connectedMarketAccounts.find(orgMarketAccount => orgMarketAccount.id === marketAccountId),
    [marketAccountId, connectedMarketAccounts]
  )
  const marketId = marketAccount?.marketId
  const location = useLocation()
  const navigate = useNavigate()

  const initialMeta = (): PaginableMeta => {
    const queryParams = readQueryParams()
    return {
      ...Meta.InitialState(),
      viewListingId: queryParams.viewListingId
    }
  }

  const [meta, setMeta] = useState<PaginableMeta>(initialMeta())
  const [loading, setLoading] = useState(false)
  const [selectionState, setSelectionState] = useState<SelectionState>({ selection: new Set(), selectAll: false })
  const [dialogState, setDialogState] = useState<DialogState>({
    listDialogOpen: false,
    delistDialogOpen: false,
    relistDialogOpen: false,
    purgeDialogOpen: false
  })
  const [data, setData] = useState([])
  const [requiresAttention, setRequiresAttention] = useState(false)
  const [columnDialogOpen, setColumnDialogOpen] = useState(false)
  const [enabledColumns, setEnabledColumns] = useState<Record<string, boolean>>(
    () => (columnStorageKey && getFromStorage(columnStorageKey, true)) || {}
  )

  const attentionPollerRef = useRef(null)
  useEffect(() => {
    if (!marketAccountId || !marketId) return () => {}

    const attentionPoller = new Poller(
      () => {
        const validChannels = [CHANNELS.GOODWILLFINDS.id, CHANNELS.ETSY.id, CHANNELS.AMAZON.id, CHANNELS.DEPOP.id]
        if (validChannels.includes(marketId)) {
          return Client.marketRequiresAttention({ marketAccountId })
        }

        throw Error(`Unsupported market: ${marketId}`)
      },
      () => ({}),
      (dataAttnReq, callback) => {
        setRequiresAttention(prevRequiresAttention =>
          prevRequiresAttention === dataAttnReq.requiresAttention
            ? prevRequiresAttention
            : dataAttnReq.requiresAttention
        )
        callback()
      }
    )

    attentionPollerRef.current = attentionPoller

    attentionPoller.startImmediately()

    // cancel poller when component unmounts
    return () => {
      attentionPoller.cancel()
    }
  }, [marketAccountId, marketId])

  useEffect(() => {
    if (!marketId) return () => {}

    // websocket connection for listing state updates
    const subscription = newConnection(marketId, d => {
      setData(dataOld => {
        return updateFromProduct({ data: dataOld }, d).data
      })
    })

    return () => {
      subscription.unsubscribe()
    }
  }, [marketId])

  const fetchData = useCallback(
    async metaParam => {
      if (!marketAccountId) return

      const buildRequestParameters = () => {
        const state = metaParam.state || (metaParam.tab === 'inactive-all' ? Meta.InactiveStates : Meta.ActiveStates)

        return {
          ...metaParam,
          ...Meta.clearUnusedDates(metaParam),
          state
        }
      }

      setLoading(true)
      try {
        const params = cleanParams(buildRequestParameters())
        const res = await listingIndexFetchMethod(marketAccountId, params)
        attentionPollerRef?.current?.resetJustTimer()
        setMeta(oldMeta => {
          const { tab, state } = oldMeta
          const newMeta = {
            ...oldMeta,
            ...res.data.meta
          }
          newMeta.state = state
          newMeta.tab = tab
          newMeta.userId = res.data.meta.userId ? [].concat(res.data.meta.userId).join(',') : null

          return newMeta
        })

        setSelectionState({ selection: new Set(), selectAll: false })
        setData(res.data.listings.map(s => ({ ...s, _id: s.id })))
      } catch (e) {
        Sentry.captureException(e)
      } finally {
        setLoading(false)
      }
    },
    [listingIndexFetchMethod, marketAccountId]
  )

  useEffect(() => {
    if (!marketAccountId) return

    fetchData(meta)
    // TODO(goodwillfinds): make sure this is safe to do https://app.asana.com/0/1204859485810598/1206731189234157/f
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onMetaChangeStable = useCallback(
    (k, v, refetch = true) => {
      setMeta(oldMeta => {
        const newMeta = {
          ...oldMeta,
          page: 1,
          cursor: null,
          [k]: v
        }

        if (refetch) fetchData(newMeta)

        return newMeta
      })
    },
    [fetchData]
  )

  const openDrawer = useCallback(listingId => {
    setMeta(oldMeta => ({ ...oldMeta, viewListingId: listingId }))
    replaceQueryParams({ viewListingId: listingId })
  }, [])

  const closeDrawer = useCallback(() => {
    openDrawer(null)
  }, [openDrawer])

  const isActive = !meta.tab || meta.tab === 'active-all'
  const columns = useMemo(() => {
    const builtColumns = tableColumns(
      meta,
      onMetaChangeStable,
      ['active-all', 'inactive-all', null].includes(meta.tab) ? isActive : null,
      openDrawer,
      marketAccountId
    )

    if (!columnStorageKey) return builtColumns

    return applyEnabledColumns(builtColumns, enabledColumns)
  }, [columnStorageKey, enabledColumns, isActive, marketAccountId, meta, onMetaChangeStable, openDrawer, tableColumns])

  const resetFilters = useCallback(() => {
    setSelectionState({ selection: new Set(), selectAll: false })
    const blankMeta = Meta.BlankState({ meta }).meta as PaginableMeta
    setMeta(blankMeta)
    Meta.Persist(blankMeta)
    fetchData(blankMeta)
  }, [fetchData, meta])

  const isSelected = useCallback(key => selectionState.selection.has(key), [selectionState.selection])

  const onToggleAll = useCallback(() => {
    setSelectionState(oldSelectionState => ({
      selection: oldSelectionState.selectAll ? new Set() : new Set(data.map(d => d._id)),
      selectAll: !oldSelectionState.selectAll
    }))
  }, [data])

  const onToggleSelection = useCallback(key => {
    setSelectionState(oldSelectionState => {
      const selection = new Set(oldSelectionState.selection)

      if (selection.has(key)) {
        selection.delete(key)
      } else {
        selection.add(key)
      }

      return { ...oldSelectionState, selection }
    })
  }, [])

  const onStateSwitcherChange = useCallback(
    (tabId, states) => {
      setSelectionState({ selection: new Set(), selectAll: false })
      setMeta(oldMeta => {
        const newMeta = {
          ...oldMeta,
          tab: tabId || 'active-all',
          state: states || null,
          page: 1,
          perPage: oldMeta.perPage || DEFAULT_PER_PAGE,
          sort: 'created_at.desc'
        }

        fetchData(newMeta)

        return newMeta
      })
    },
    [fetchData]
  )

  // TODO: memoize
  const getAvailableActions = (selectionManager: SelectionManager) => {
    const { tab } = meta
    const disabledMarkets = [CHANNELS.GOODWILLFINDS.id, CHANNELS.ETSY.id]

    if (tab === 'inactive-all') return []

    const actions = []
    if (!currentUser.permissions.includes('listings.manage')) {
      return actions
    }

    const delistableCount = selectionManager.getDelistableCount()
    const listableCount = selectionManager.getListableCount()
    const relistableCount = selectionManager.getRelistableCount()
    const reviewableCount = selectionManager.getReviewableCount()
    const recyclableCount = selectionManager.getRecyclableCount()

    if (
      marketId === CHANNELS.DEPOP.id &&
      currentUser.permissions.includes('products.post') &&
      (tab === 'attention-required' || tab === 'inactive-pending') &&
      reviewableCount &&
      config.LISTING_REVIEW() &&
      config.DEPOP_INTEGRATION()
    ) {
      const listingIds = selectionManager.reviewable.map(listing => listing.id)
      const [currentListingId] = listingIds

      actions.push({
        humanReadableName: `Review (${reviewableCount})`,
        onClick: () => {
          trackBulkReviewClickEvent({
            marketName: 'Depop',
            connectedMarketAccounts,
            count: reviewableCount,
            marketAccountId,
            tab
          })

          navigate(`/listings/depop/${currentListingId}/edit`, {
            state: {
              listingManagerUrl: pushUrl(cleanParams(meta)),
              listingIds,
              marketAccountId
            }
          })
        },
        intent: Intent.PRIMARY
      })
    }

    if (marketId === CHANNELS.DEPOP.id && recyclableCount) {
      actions.push({
        humanReadableName: `Recycle (${recyclableCount})`,
        onClick: () => setDialogState({ ...dialogState, purgeDialogOpen: true }),
        intent: Intent.WARNING,
        icon: IconNames.REPEAT
      })
    }

    if (delistableCount) {
      actions.push({
        humanReadableName: `Delist (${delistableCount})`,
        onClick: () => setDialogState({ ...dialogState, delistDialogOpen: true }),
        intent: Intent.DANGER,
        icon: IconNames.EXCLUDE_ROW
      })
    }

    // GoodwillFinds & Etsy no longer support listing/relisting
    if (disabledMarkets.includes(marketAccount.marketId)) {
      return actions
    }

    // TODO(ian): make the market exclusion logic more generic, handle it higher up
    if (listableCount) {
      actions.push({
        humanReadableName: `List (${listableCount})`,
        onClick: () => setDialogState({ ...dialogState, listDialogOpen: true }),
        intent: Intent.SUCCESS,
        icon: IconNames.TICK
      })
    }

    if (relistableCount) {
      actions.push({
        humanReadableName: `Relist (${relistableCount})`,
        onClick: () => setDialogState({ ...dialogState, relistDialogOpen: true }),
        intent: Intent.PRIMARY,
        icon: IconNames.SOCIAL_MEDIA
      })
    }

    return actions
  }

  const onDialogClose = (dialogKey: keyof DialogState) => {
    setDialogState({ ...dialogState, [dialogKey]: false })
    setSelectionState({ ...selectionState, selection: new Set(), selectAll: false })
  }

  const onBulkActionDialogSubmit = async () => {
    await fetchData(meta)
  }

  // TODO: memoize https://app.asana.com/0/1206184798330168/1206962634973833
  const selectionManager = new SelectionManager()
  selectionManager.setSelectionData(Array.from(selectionState.selection), data)

  if (!marketAccountId || !marketAccount) {
    return (
      <div className="container" style={{ maxWidth: '100%' }}>
        <Callout intent={Intent.WARNING} title="No channel account linked">
          Link an account for this channel under Settings → Channels, then reload this page. For local dev, run{' '}
          <code>docker compose exec backend bundle exec rails db:seed</code> (or the Amazon integration seed).
        </Callout>
      </div>
    )
  }

  return (
    <div className="container" style={{ maxWidth: '100%' }}>
      {BulkListingDialogs && (
        <BulkListingDialogs
          dialogState={dialogState}
          selectionManager={selectionManager}
          onClose={onDialogClose}
          marketAccountId={marketAccount.id}
          onSubmitted={onBulkActionDialogSubmit}
          channelId={channelId}
        />
      )}

      <Widget title={`Manage ${marketAccount.humanReadableName} Listings`}>
        <div style={{ display: 'flex' }}>
          <StateSwitcher
            disabled={loading}
            onChange={onStateSwitcherChange}
            sections={TabStates}
            selectedTabId={meta.tab || 'active-all'}
          />
          <Button
            active={meta.tab === 'attention-required'}
            icon={requiresAttention ? IconNames.ERROR : null}
            intent={requiresAttention ? Intent.DANGER : Intent.NONE}
            disabled={loading}
            minimal
            onClick={() => onStateSwitcherChange('attention-required', Meta.FailedStates)}
            style={{ marginLeft: 20 }}
          >
            Attention Required
          </Button>
          <div style={{ marginLeft: 'auto' }}>
            <Button onClick={resetFilters} disabled={loading}>
              Reset Filters
            </Button>
            {columnStorageKey && (
              <>
                {' '}
                <Button
                  intent={visibleCustomizableColumnCount(columns) <= 1 ? Intent.DANGER : Intent.NONE}
                  onClick={() => setColumnDialogOpen(true)}
                >
                  Customize Columns
                </Button>
              </>
            )}
          </div>
        </div>
        <Divider style={{ margin: '20px 0 10px' }} />
        {columnStorageKey && (
          <ColumnDialog
            columns={columns}
            storageKey={columnStorageKey}
            isOpen={columnDialogOpen}
            enabledColumns={enabledColumns}
            onDialogClose={() => setColumnDialogOpen(false)}
            onEnabledColumnChange={setEnabledColumns}
          />
        )}
        <Drawer
          size={DrawerSize.STANDARD}
          isOpen={!!meta.viewListingId}
          enforceFocus
          hasBackdrop
          usePortal
          style={{ overflow: 'scroll' }}
          autoFocus
          canEscapeKeyClose
          canOutsideClickClose
          onClose={closeDrawer}
        >
          <ShowListingComponent
            key={`${meta.viewListingId}-${location.key}`}
            refreshData={() => {
              // reset the attentionPoller fully because the underlying data (the listings which need attention)
              // could have changed by deleting a listing
              attentionPollerRef?.current?.reset()
              return fetchData(meta)
            }}
            onClose={closeDrawer}
            listingId={meta.viewListingId}
            marketAccount={marketAccount}
            onChange={listingId => openDrawer(listingId)}
          />
        </Drawer>

        <PopIn
          overlayProps={{
            hasBackdrop: false,
            isOpen: selectionState?.selection?.size > 0,
            canEscapeKeyClose: true,
            canOutsideClickClose: false,
            onClose: () => setSelectionState({ selection: new Set(), selectAll: false })
          }}
        >
          <BulkActionBar
            title={`${selectionState?.selection?.size} listing${S(selectionState?.selection?.size)} selected`}
            actions={getAvailableActions(selectionManager)}
          />
        </PopIn>

        <Table
          columns={columns}
          loading={loading}
          meta={meta}
          data={data}
          isSelected={isSelected}
          onMetaChange={onMetaChangeStable}
          toggleAll={onToggleAll}
          toggleSelection={onToggleSelection}
          selectAll={selectionState.selectAll}
          noDataText={meta.tab === 'attention-required' && 'No listings requiring action. Nice work! 🎉'}
        />
      </Widget>
    </div>
  )
}
